import * as jose from 'jose'
import { supabase } from './supabase'
import type { CompanyOption, User } from '../types'
import type { B2BBuyer } from '../types/b2b'
import { getActiveCompanyId, resetActiveCompanyCache } from './activeCompany'
import { createContextLogger } from './logger'
import { clearSessionCaches } from './sessionCache'
import { flushB2BCartOnLogout } from '../hooks/useB2BCart'
import { dispatchSessionEnd } from './tenantStorage'
import { logger } from './logger'
import { clearStoredSupabaseToken, setStoredSupabaseToken } from './supabaseAuthToken'

const log = createContextLogger('Auth')

let _logoutInProgress = false

export function isLogoutInProgress(): boolean {
  return _logoutInProgress
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!SUPABASE_URL) throw new Error('VITE_SUPABASE_URL is required')
if (!SUPABASE_ANON_KEY) throw new Error('VITE_SUPABASE_ANON_KEY is required')

const AUTH_FUNCTION_URL = `${SUPABASE_URL}/functions/v1/auth-login`

export interface LoginResult {
  success: boolean
  user?: User
  buyer?: B2BBuyer
  token?: string
  error?: string
  redirectTo?: string
}

async function callAuthFunction(
  body: Record<string, unknown>,
  bearerToken?: string | null,
): Promise<Response> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'apikey': SUPABASE_ANON_KEY,
    'Authorization': `Bearer ${bearerToken ?? SUPABASE_ANON_KEY}`,
  }

  return fetch(AUTH_FUNCTION_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  })
}

export function buildEdgeFunctionHeaders(bearerToken?: string | null): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'apikey': SUPABASE_ANON_KEY,
    'Authorization': `Bearer ${bearerToken ?? SUPABASE_ANON_KEY}`,
  }
}

async function parseAuthResponse(res: Response): Promise<Record<string, unknown>> {
  const text = await res.text()
  if (!text) return {}
  try {
    return JSON.parse(text) as Record<string, unknown>
  } catch {
    return { error: text }
  }
}

export async function hashPassword(password: string): Promise<string> {
  const token = localStorage.getItem('auth_token')
  if (!token) throw new Error('Authentication required for password hashing')

  const res = await callAuthFunction({ action: 'hash', password }, token)
  const data = await res.json()
  if (!res.ok || !data.success) throw new Error(data.error || 'Failed to hash password')
  return data.hash as string
}

export async function loginUser(email: string, password: string): Promise<LoginResult> {
  try {
    const res = await callAuthFunction({ action: 'login', email, password })
    const data = await res.json()

    if (!data.success) {
      log.error('Login failed', { error: data.error })
      return { success: false, error: data.error || 'Login failed. Please try again.' }
    }

    if (data.user) {
      localStorage.setItem('auth_token', data.token)
      setStoredSupabaseToken(data.sb_token as string | null | undefined)
      localStorage.setItem('user', JSON.stringify(data.user))
      localStorage.removeItem('b2b_buyer')

      return {
        success: true,
        user: data.user as User,
        token: data.token as string,
        redirectTo: data.redirectTo as string,
      }
    }

    if (data.buyer) {
      localStorage.setItem('auth_token', data.token)
      setStoredSupabaseToken(data.sb_token as string | null | undefined)
      localStorage.setItem('b2b_buyer', JSON.stringify(data.buyer))
      localStorage.removeItem('user')

      return {
        success: true,
        buyer: data.buyer as B2BBuyer,
        token: data.token as string,
        redirectTo: data.redirectTo as string,
      }
    }

    return { success: false, error: 'Unexpected response from auth service.' }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    log.error('Login exception', { error: message })
    return {
      success: false,
      error: message.includes('Failed to fetch')
        ? 'Cannot reach the auth service. Check your connection and try again.'
        : 'An unexpected error occurred.',
    }
  }
}

export async function registerUser(
  email: string,
  password: string,
  full_name: string,
  role: string,
  admin_id?: string,
): Promise<{ success: boolean; user?: User; error?: string }> {
  try {
    const { data: existingUser } = await supabase
      .from('users')
      .select('id')
      .eq('email', email)
      .maybeSingle()

    if (existingUser) return { success: false, error: 'Email already exists.' }

    const password_hash = await hashPassword(password)

    const { data: user, error } = await supabase
      .from('users')
      .insert({ email, password_hash, full_name, role, admin_id: admin_id || null, is_active: true })
      .select()
      .single()

    if (error) {
      log.error('Registration failed', { error: error.message })
      return { success: false, error: 'Registration failed. Please try again.' }
    }

    return { success: true, user: { ...user, password_hash: undefined } }
  } catch (error) {
    log.error('Registration exception', { error: String(error) })
    return { success: false, error: 'An unexpected error occurred.' }
  }
}

export async function getCurrentUser(): Promise<User | null> {
  try {
    const token = localStorage.getItem('auth_token')
    const userStr = localStorage.getItem('user')

    const buyerStr = localStorage.getItem('b2b_buyer')
    if (token && buyerStr && !userStr) return null
    if (!token || !userStr) return null

    const decoded = await verifyToken(token)
    if (!decoded) {
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user')
      return null
    }

    const user = JSON.parse(userStr) as User

    const { data: freshUser, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', user.id)
      .eq('is_active', true)
      .maybeSingle()

    if (error) {
      log.warn('Failed to fetch fresh user data, using cached user', { error: error.message })
      return user
    }

    if (!freshUser) {
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user')
      return null
    }

    const userWithoutPassword = { ...freshUser, password_hash: undefined }
    localStorage.setItem('user', JSON.stringify(userWithoutPassword))
    return userWithoutPassword
  } catch (error) {
    log.error('Get current user error', { error: String(error) })
    return null
  }
}

export async function getCurrentBuyer(): Promise<B2BBuyer | null> {
  try {
    const token = localStorage.getItem('auth_token')
    const buyerStr = localStorage.getItem('b2b_buyer')
    if (!token || !buyerStr) return null

    const decoded = await verifyToken(token)
    if (!decoded || decoded.role !== 'buyer') return null

    const buyer = JSON.parse(buyerStr) as B2BBuyer

    const { data: freshBuyer, error } = await supabase
      .from('b2b_buyers')
      .select('*')
      .eq('id', buyer.id)
      .eq('is_active', true)
      .maybeSingle()

    if (error) {
      log.warn('Failed to fetch fresh buyer data, using cached buyer', { error: error.message })
      return buyer
    }

    if (!freshBuyer || freshBuyer.account_status !== 'active') {
      localStorage.removeItem('auth_token')
      localStorage.removeItem('b2b_buyer')
      return null
    }

    const buyerWithoutPassword: B2BBuyer = { ...freshBuyer, password_hash: undefined }
    localStorage.setItem('b2b_buyer', JSON.stringify(buyerWithoutPassword))
    return buyerWithoutPassword
  } catch (error) {
    log.error('Get current buyer error', { error: String(error) })
    return null
  }
}

export async function logoutUser(): Promise<void> {
  _logoutInProgress = true

  try {
    const userStr = localStorage.getItem('user')
    const buyerStr = localStorage.getItem('b2b_buyer')

    if (userStr) {
      try {
        const user = JSON.parse(userStr)
        await supabase.from('activity_logs').insert({
          admin_id: getActiveCompanyId() || user.admin_id || user.id,
          user_id: user.id,
          action_type: 'auth',
          entity_type: 'user',
          entity_id: user.id,
          description: `${user.full_name || user.email} logged out`,
          metadata: { email: user.email },
          created_at: new Date().toISOString()
        })
      } catch (err) {
        log.error('Failed to log logout activity', { error: String(err) })
      }
    } else if (buyerStr) {
      try {
        const buyer = JSON.parse(buyerStr)
        await flushB2BCartOnLogout({ id: buyer.id, admin_id: buyer.admin_id })
        await supabase.from('activity_logs').insert({
          admin_id: buyer.admin_id,
          user_id: buyer.admin_id,
          action_type: 'auth',
          entity_type: 'b2b_buyer',
          entity_id: buyer.id,
          description: `B2B buyer ${buyer.trade_name || buyer.email} logged out`,
          metadata: { email: buyer.email },
          created_at: new Date().toISOString()
        })
      } catch (err) {
        log.error('Failed to log buyer logout activity', { error: String(err) })
      }
    }

    dispatchSessionEnd()
    clearTokenRefreshTimer()
    logger.setUserId(null)
    clearSessionCaches()

    localStorage.removeItem('auth_token')
    localStorage.removeItem('user')
    localStorage.removeItem('b2b_buyer')
    clearStoredSupabaseToken()
  } finally {
    _logoutInProgress = false
  }
}

export async function updateUserProfile(
  userId: string,
  updates: Partial<User>,
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('users')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', userId)

    if (error) {
      log.error('Update profile error', { error: error.message })
      return { success: false, error: 'Failed to update profile.' }
    }

    const userStr = localStorage.getItem('user')
    if (userStr) {
      try {
        const user = JSON.parse(userStr)
        localStorage.setItem('user', JSON.stringify({ ...user, ...updates }))
      } catch {
        localStorage.setItem('user', JSON.stringify(updates))
      }
    }

    return { success: true }
  } catch (error) {
    log.error('Update profile exception', { error: String(error) })
    return { success: false, error: 'An unexpected error occurred.' }
  }
}

const TOKEN_REFRESH_THRESHOLD_MINUTES = 60
let tokenRefreshTimer: ReturnType<typeof setTimeout> | null = null
let tokenRefreshListeners: Array<(token: string) => void> = []

export function getTokenExpiration(token: string): Date | null {
  try {
    const payload = jose.decodeJwt(token)
    if (payload.exp) return new Date(payload.exp * 1000)
    return null
  } catch {
    return null
  }
}

export function isTokenExpiringSoon(thresholdMinutes = TOKEN_REFRESH_THRESHOLD_MINUTES): boolean {
  const token = localStorage.getItem('auth_token')
  if (!token) return true
  const expiration = getTokenExpiration(token)
  if (!expiration) return true
  return expiration.getTime() - Date.now() < thresholdMinutes * 60 * 1000
}

export function isTokenExpired(): boolean {
  const token = localStorage.getItem('auth_token')
  if (!token) return true
  const expiration = getTokenExpiration(token)
  if (!expiration) return true
  return Date.now() >= expiration.getTime()
}

/**
 * Decode-only token verification for the client.
 * Checks structure and expiry locally. Signature verification happens
 * server-side in the auth-login edge function.
 */
export async function verifyToken(
  token: string,
): Promise<{ userId: string; role: string; admin_id: string } | null> {
  try {
    const payload = jose.decodeJwt(token)
    if (!payload.userId && !payload.sub) return null
    if (payload.exp && Date.now() >= payload.exp * 1000) return null
    return {
      userId: (payload.userId as string) || (payload.sub as string) || '',
      role: (payload.role as string) || '',
      admin_id: (payload.admin_id as string) || '',
    }
  } catch {
    return null
  }
}

export async function refreshToken(): Promise<string | null> {
  try {
    const currentToken = localStorage.getItem('auth_token')
    if (!currentToken) {
      log.warn('Cannot refresh token: no token found')
      return null
    }

    const res = await callAuthFunction({ action: 'refresh' }, currentToken)
    const data = await res.json()

    if (!res.ok || !data.success) {
      log.warn('Token refresh rejected by server', { error: data.error })
      if (res.status === 401) {
        localStorage.removeItem('auth_token')
        localStorage.removeItem('user')
        localStorage.removeItem('b2b_buyer')
        clearStoredSupabaseToken()
      }
      return null
    }

    const newToken = data.token as string
    localStorage.setItem('auth_token', newToken)
    setStoredSupabaseToken(data.sb_token as string | null | undefined)
    log.info('Token refreshed successfully')

    tokenRefreshListeners.forEach(listener => {
      try {
        listener(newToken)
      } catch (err) {
        log.error('Token refresh listener error', { error: String(err) })
      }
    })

    return newToken
  } catch (error) {
    log.error('Token refresh failed', { error: String(error) })
    return null
  }
}

export interface CompanyListResult {
  companies: CompanyOption[]
  /** False when the list could not be loaded, so callers can tell a failed
   *  request apart from an admin who genuinely has no company access. */
  ok: boolean
}

let companiesRequest: Promise<CompanyListResult> | null = null

/**
 * Companies the signed-in admin may act as: their own plus any granted by the
 * super admin. The caller identity comes from the verified token server-side,
 * never from the request body.
 *
 * Concurrent callers share a single request. StrictMode mounts the auth effect
 * twice in development, and the two identical in-flight POSTs raced each other.
 * Only in-flight calls are shared, so a switch or a revoked grant is never
 * served from a stale result.
 */
export function fetchMyCompanies(): Promise<CompanyListResult> {
  if (!companiesRequest) {
    companiesRequest = requestMyCompanies().finally(() => {
      companiesRequest = null
    })
  }

  return companiesRequest
}

async function requestMyCompanies(): Promise<CompanyListResult> {
  try {
    const token = await getValidToken()
    if (!token) return { companies: [], ok: true }

    const res = await callAuthFunction({ action: 'companies' }, token)
    const data = await parseAuthResponse(res)

    if (!res.ok || !data.success) {
      log.warn('Failed to load company access list', { error: data.error })
      return { companies: [], ok: false }
    }

    return { companies: (data.companies as CompanyOption[]) ?? [], ok: true }
  } catch (error) {
    // Network-level failure such as offline, DNS or a torn-down connection.
    // Recoverable: the caller keeps whatever list it already had.
    log.warn('Company list request did not complete', { error: String(error) })
    return { companies: [], ok: false }
  }
}

/**
 * Re-mints the session tokens against another company. The server verifies the
 * grant, so a rejected switch leaves the existing tokens untouched.
 */
export async function switchCompany(
  targetAdminId: string,
): Promise<{ success: boolean; error?: string }> {
  const token = await getValidToken()
  if (!token) return { success: false, error: 'Your session has expired. Please sign in again.' }

  try {
    const res = await callAuthFunction(
      { action: 'switch-company', target_admin_id: targetAdminId },
      token,
    )
    const data = await parseAuthResponse(res)

    if (!res.ok || !data.success) {
      log.warn('Company switch rejected', { error: data.error })
      return { success: false, error: (data.error as string) || 'Unable to switch company.' }
    }

    localStorage.setItem('auth_token', data.token as string)
    setStoredSupabaseToken(data.sb_token as string | null | undefined)
    resetActiveCompanyCache()

    return { success: true }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    log.error('Company switch exception', { error: message })
    return {
      success: false,
      error: message.includes('Failed to fetch')
        ? 'Cannot reach the auth service. Check your connection and try again.'
        : 'An unexpected error occurred.',
    }
  }
}

export interface CompanyAccessState {
  companies: CompanyOption[]
  granted: string[]
}

/** Super-admin only: every company plus the ones this admin already holds. */
export async function listCompanyAccess(
  userId: string,
): Promise<{ success: boolean; data?: CompanyAccessState; error?: string }> {
  return callCompanyAccessAction({ action: 'list-company-access', user_id: userId }, (data) => ({
    companies: (data.companies as CompanyOption[]) ?? [],
    granted: (data.granted as string[]) ?? [],
  }))
}

/** Super-admin only: let `userId` act as `companyAdminId`. */
export async function grantCompanyAccess(
  userId: string,
  companyAdminId: string,
): Promise<{ success: boolean; error?: string }> {
  return callCompanyAccessAction({
    action: 'grant-company-access',
    user_id: userId,
    company_admin_id: companyAdminId,
  })
}

/** Super-admin only: withdraw a previously granted company. */
export async function revokeCompanyAccess(
  userId: string,
  companyAdminId: string,
): Promise<{ success: boolean; error?: string }> {
  return callCompanyAccessAction({
    action: 'revoke-company-access',
    user_id: userId,
    company_admin_id: companyAdminId,
  })
}

async function callCompanyAccessAction<T>(
  body: Record<string, unknown>,
  map?: (data: Record<string, unknown>) => T,
): Promise<{ success: boolean; data?: T; error?: string }> {
  const token = await getValidToken()
  if (!token) return { success: false, error: 'Your session has expired. Please sign in again.' }

  try {
    const res = await callAuthFunction(body, token)
    const data = await parseAuthResponse(res)

    if (!res.ok || !data.success) {
      log.warn('Company access action failed', { action: body.action, error: data.error })
      return { success: false, error: (data.error as string) || 'Request failed.' }
    }

    return { success: true, data: map ? map(data) : undefined }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    log.error('Company access action exception', { action: body.action, error: message })
    return {
      success: false,
      error: message.includes('Failed to fetch')
        ? 'Cannot reach the auth service. Check your connection and try again.'
        : 'An unexpected error occurred.',
    }
  }
}

export async function getValidToken(): Promise<string | null> {
  const token = localStorage.getItem('auth_token')
  if (!token) return null

  if (isTokenExpired()) {
    log.warn('Token expired, clearing session')
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user')
    clearStoredSupabaseToken()
    return null
  }

  if (isTokenExpiringSoon()) {
    log.info('Token expiring soon, refreshing...')
    const newToken = await refreshToken()
    return newToken || token
  }

  return token
}

export function onTokenRefresh(listener: (token: string) => void): () => void {
  tokenRefreshListeners.push(listener)
  return () => {
    tokenRefreshListeners = tokenRefreshListeners.filter(l => l !== listener)
  }
}

export function setupTokenRefreshTimer(): () => void {
  if (tokenRefreshTimer) clearTimeout(tokenRefreshTimer)

  const scheduleNextRefresh = () => {
    const token = localStorage.getItem('auth_token')
    if (!token) return

    const expiration = getTokenExpiration(token)
    if (!expiration) return

    const timeUntilExpiry = expiration.getTime() - Date.now()
    const refreshTime = Math.max(
      timeUntilExpiry - TOKEN_REFRESH_THRESHOLD_MINUTES * 60 * 1000,
      60 * 1000,
    )

    tokenRefreshTimer = setTimeout(async () => {
      const newToken = await refreshToken()
      if (newToken) scheduleNextRefresh()
    }, refreshTime)

    log.info('Token refresh scheduled', {
      refreshIn: Math.round(refreshTime / 1000 / 60) + ' minutes',
    })
  }

  scheduleNextRefresh()

  return () => {
    if (tokenRefreshTimer) {
      clearTimeout(tokenRefreshTimer)
      tokenRefreshTimer = null
    }
  }
}

export function clearTokenRefreshTimer(): void {
  if (tokenRefreshTimer) {
    clearTimeout(tokenRefreshTimer)
    tokenRefreshTimer = null
  }
  tokenRefreshListeners = []
}

export function getAuthHeaders(): Record<string, string> | null {
  const token = localStorage.getItem('auth_token')
  if (!token) return null
  return buildEdgeFunctionHeaders(token)
}

export async function getValidAuthHeaders(forcedToken?: string): Promise<Record<string, string> | null> {
  if (_logoutInProgress) return null
  const token = forcedToken || await getValidToken()
  if (!token) return null
  return buildEdgeFunctionHeaders(token)
}

/**
 * Fail-closed edge-function headers: validates and (if needed) refreshes the
 * token before every call, and throws a user-friendly error when no valid
 * session exists. This replaces the removed synchronous `getEdgeFunctionHeaders`,
 * which sent whatever (possibly expired) token was in localStorage.
 */
export async function getValidAuthHeadersOrThrow(): Promise<Record<string, string>> {
  const headers = await getValidAuthHeaders()
  if (!headers) throw new Error('Your session has expired. Please sign in again.')
  return headers
}
